import { useTranslation } from 'react-i18next';
import { Button, Modal } from './ui';
import type { ButtonVariant } from './ui';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'default' | 'warning' | 'danger';
}

// Map the legacy variant names onto the shared Button variants.
const CONFIRM_BUTTON_VARIANT: Record<NonNullable<ConfirmModalProps['variant']>, ButtonVariant> = {
  default: 'primary',
  warning: 'amber',
  danger: 'danger',
};

/**
 * App-wide confirmation dialog (mounts via conditional rendering).
 *
 * Variant B refactor: now a thin wrapper over the shared `<Modal>` primitive,
 * inheriting focus trap, ESC-to-close, and backdrop-click-to-close from there.
 *
 * Used by the in-app flows (`QuestionCard` hint reveal, `BillingPage` cancel /
 * withdrawal). The admin variant lives at `components/admin/ConfirmModal.tsx`
 * and uses an explicit `isOpen` prop instead of conditional rendering.
 */
export default function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  variant = 'default',
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const resolvedConfirm = confirmLabel ?? t('components.confirmModal.confirm');
  const resolvedCancel = cancelLabel ?? t('components.confirmModal.cancel');

  return (
    <Modal
      open
      onClose={onCancel}
      ariaLabelledBy="confirm-modal-title"
      ariaDescribedBy="confirm-modal-message"
      size="sm"
    >
      <Modal.Header id="confirm-modal-title">{title}</Modal.Header>
      <Modal.Body>
        <p id="confirm-modal-message">{message}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {resolvedCancel}
        </Button>
        <Button type="button" variant={CONFIRM_BUTTON_VARIANT[variant]} onClick={onConfirm} autoFocus>
          {resolvedConfirm}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
